import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

export default class extends Controller {
  static values = { 
    labels: Array, 
    values: Array,
    type: String,
    title: String,
    config: Object
  }

  connect() {
    if (typeof Chart === 'undefined') {
      console.error("Chart.js is not available!")
      return
    }
    
    // Add a small delay to ensure the element is fully rendered
    setTimeout(() => {
      this.createChart()
    }, 100)
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  createChart() {
    const canvas = this.element
    const ctx = canvas.getContext("2d")
    
    // Destroy existing chart if it exists
    if (this.chart) {
      this.chart.destroy()
    }

    const chartType = this.typeValue || "bar"
    const labels = this.labelsValue || []
    const values = this.valuesValue || []
    const title = this.titleValue || ""
    const customConfig = this.configValue || {}
    

    // Default chart configuration
    let config = {
      type: chartType,
      data: {
        labels: labels,
        datasets: [{
          label: title,
          data: values,
          backgroundColor: "rgba(0, 187, 148, 0.3)",
          borderColor: "rgba(0, 187, 148, 0.8)",
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: '件'
            }
          }
        }
      }
    }

    // Merge custom config if provided
    if (Object.keys(customConfig).length > 0) {
      // Handle data.datasets specially to avoid conflicts
      if (customConfig.data && customConfig.data.datasets) {
        // Merge datasets properties instead of replacing the entire datasets array
        const customDatasets = customConfig.data.datasets[0] || {}
        config.data.datasets[0] = { ...config.data.datasets[0], ...customDatasets }
        // Remove data.datasets from customConfig to avoid conflicts
        const { datasets, ...restData } = customConfig.data
        customConfig.data = restData
      }
      
      config = this.deepMerge(config, customConfig)
      // Convert string functions to actual functions
      config = this.convertStringFunctions(config)
    }

    // Customize based on chart type and element ID
    if (canvas.id === "skillRewardChart") {
      config.options.scales.x = {
        title: {
          display: true,
          text: '万円'
        }
      }
    } else if (canvas.id === "jobSourceChart") {
      config.options.indexAxis = 'y'
      config.options.scales.x = {
        beginAtZero: true,
        title: {
          display: true,
          text: '件'
        }
      }
      config.options.scales.y = {
        title: {
          display: false
        }
      }
    } else if (chartType === "pie") {
      // Pie charts don't need scales
      delete config.options.scales
      // Ensure pie chart data structure is correct
      if (config.data.datasets && config.data.datasets.length > 0) {
        config.data.datasets[0].data = values
      }
    }

    try {
      this.chart = new Chart(ctx, config)
    } catch (error) {
      console.error("Error creating chart:", error)
      console.error("Chart config:", config)
    }
  }

  deepMerge(target, source) {
    const result = { ...target }
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key])
      } else if (Array.isArray(source[key])) {
        // For arrays, replace entirely (don't merge)
        result[key] = source[key]
      } else {
        result[key] = source[key]
      }
    }
    
    return result
  }

  convertStringFunctions(obj) {
    if (typeof obj === 'string' && obj.startsWith('function(')) {
      return new Function('return ' + obj)()
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertStringFunctions(item))
    }
    
    if (typeof obj === 'object' && obj !== null) {
      const result = {}
      for (const key in obj) {
        result[key] = this.convertStringFunctions(obj[key])
      }
      return result
    }
    
    return obj
  }
}